import { badgeOutOfBounds, badgeSurface } from '@cloudsign/shared';
import type { BadgeElement, BadgeLayout } from '@cloudsign/shared';

/** Positions snap to half a millimetre. */
export const SNAP_MM = 0.5;
export const MAX_ELEMENTS = 30;
const MAX_MM = 200;

export const snap = (v: number): number => Math.round(v / SNAP_MM) * SNAP_MM;
const dotsToMm = (d: number): number => Math.round(((d * 25.4) / 300) * 100) / 100;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Printable area of the label in its printed orientation, in mm. */
export function surfaceMm(layout: Pick<BadgeLayout, 'media' | 'orientation'>): { width: number; height: number } {
  const s = badgeSurface(layout);
  return { width: dotsToMm(s.widthDots), height: dotsToMm(s.heightDots) };
}

export type PaletteKind = 'text_static' | 'text_data' | 'photo' | 'tenant_logo' | 'site_logo' | 'template_logo' | 'qr' | 'box';
export const PALETTE: PaletteKind[] = ['text_static', 'text_data', 'photo', 'tenant_logo', 'site_logo', 'template_logo', 'qr', 'box'];

export const DATA_KEYS = ['visitor_name', 'first_name', 'group_name', 'site_name', 'host_name', 'date', 'time_in', 'tenant_name'] as const;

function nextId(layout: BadgeLayout): string {
  const used = new Set(layout.elements.map((e) => e.id));
  for (let n = layout.elements.length + 1; ; n++) if (!used.has(`el-${n}`)) return `el-${n}`;
}

/** A new element of the chosen kind near the top-left, sized to fit the label. */
export function makeElement(kind: PaletteKind, layout: BadgeLayout, staticText: string): BadgeElement {
  const { width, height } = surfaceMm(layout);
  const id = nextId(layout);
  const box = (w: number, h: number) => ({
    id,
    x: 1,
    y: 1,
    w: Math.max(1, snap(Math.min(w, width - 1))),
    h: Math.max(1, snap(Math.min(h, height - 1))),
  });
  switch (kind) {
    case 'text_static':
      return { ...box(40, 8), type: 'text', source: { kind: 'static', text: staticText }, fontSize: 14, bold: false, align: 'left', shrinkToFit: true, maxLines: 1 };
    case 'text_data':
      return { ...box(40, 8), type: 'text', source: { kind: 'data', key: 'visitor_name' }, fontSize: 18, bold: true, align: 'left', shrinkToFit: true, maxLines: 1 };
    case 'photo':
    case 'tenant_logo':
    case 'site_logo':
    case 'template_logo':
      return { ...box(20, 20), type: 'image', source: kind, fit: 'contain' };
    case 'qr':
      return { ...box(16, 16), type: 'qr', source: 'sign_out' };
    case 'box':
      return { ...box(30, 12), type: 'box', strokeMm: 0.3, fill: false };
  }
}

export function addElement(layout: BadgeLayout, el: BadgeElement): BadgeLayout {
  return layout.elements.length >= MAX_ELEMENTS ? layout : { ...layout, elements: [...layout.elements, el] };
}

export function removeElement(layout: BadgeLayout, id: string): BadgeLayout {
  return { ...layout, elements: layout.elements.filter((e) => e.id !== id) };
}

/** Shallow update of one element; geometry is snapped and kept inside the 0..200 mm the contract allows. */
export function updateElement(layout: BadgeLayout, id: string, patch: Partial<BadgeElement>): BadgeLayout {
  return {
    ...layout,
    elements: layout.elements.map((e) => {
      if (e.id !== id) return e;
      const next = { ...e, ...patch } as BadgeElement;
      next.x = clamp(snap(next.x), 0, MAX_MM);
      next.y = clamp(snap(next.y), 0, MAX_MM);
      next.w = clamp(snap(next.w), 1, MAX_MM);
      next.h = clamp(snap(next.h), 1, MAX_MM);
      return next;
    }),
  };
}

/** Move by whole snap steps (arrow keys), or resize when `resize` is set. */
export function nudge(layout: BadgeLayout, id: string, dx: number, dy: number, resize: boolean): BadgeLayout {
  const el = layout.elements.find((e) => e.id === id);
  if (!el) return layout;
  return resize
    ? updateElement(layout, id, { w: el.w + dx * SNAP_MM, h: el.h + dy * SNAP_MM })
    : updateElement(layout, id, { x: el.x + dx * SNAP_MM, y: el.y + dy * SNAP_MM });
}

export const outsideIds = (layout: BadgeLayout): string[] => badgeOutOfBounds(layout);
export const sameLayout = (a: BadgeLayout, b: BadgeLayout): boolean => JSON.stringify(a) === JSON.stringify(b);
